import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import DraggableItem from 'common/DraggableItem';
import {SetValueOptions} from 'components/hook-form/helpers';
import produce from 'immer';
import {intersection, union} from 'lodash';
import React from 'react';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {useController, useFormContext} from 'react-hook-form';

type Props = {
   name: string;
   disabled?: boolean;
   options: string[];
};

type CheckboxArrayOrderable = Props & {
   value: string[];
   setValue: (v: string[]) => void;
};

const CheckboxArrayOrderable = ({
   name,
   disabled,
   options: _options,
   value,
   setValue,
}: CheckboxArrayOrderable) => {
   const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const {name, checked} = event.target;
      if (checked) {
         return setValue([...value, name]);
      }
      setValue(value.filter((value) => name !== value));
   };

   const [options, setOptions] = React.useState(() => union(value, _options));

   const reorderValues = React.useCallback(
      (options: string[]) => {
         const newVal = intersection(options, value);
         setValue(newVal);
      },
      [value, setValue]
   );

   const moveItem = React.useCallback(
      (dragIndex: number, hoverIndex: number) => {
         const dragItem = options[dragIndex];
         const newOptions = produce(options, (draft) => {
            draft.splice(dragIndex, 1);
            draft.splice(hoverIndex, 0, dragItem);
         });
         setOptions(newOptions);
         reorderValues(newOptions);
      },
      [options, setOptions, reorderValues]
   );

   // const moveItem = React.useCallback(
   //    (dragIndex: number, hoverIndex: number) => {
   //       const dragItem = value[dragIndex];
   //       setValue(
   //          produce(value, (draft) => {
   //             draft.splice(dragIndex, 1);
   //             draft.splice(hoverIndex, 0, dragItem);
   //          })
   //       );
   //    },
   //    [value, setValue]
   // );

   return (
      <DndProvider backend={HTML5Backend}>
         <FormControl component="fieldset" disabled={disabled}>
            <FormGroup>
               {options.map((opt, index) => {
                  const checked = value?.includes(opt);
                  return (
                     <DraggableItem
                        key={opt}
                        id={opt}
                        index={index}
                        type={name}
                        moveItem={moveItem}
                     >
                        <FormControlLabel
                           label={opt}
                           control={
                              <Checkbox
                                 disableRipple
                                 checked={checked}
                                 onChange={handleChange}
                                 name={opt}
                              />
                           }
                        />
                     </DraggableItem>
                  );
               })}
            </FormGroup>
         </FormControl>
      </DndProvider>
   );
};

const HookCheckboxArrayOrderable = (props: Props) => {
   const {
      field: {value},
   } = useController({
      name: props.name,
   });
   const methods = useFormContext();
   const setValue = React.useCallback(
      (val: any) => methods.setValue(props.name, val, SetValueOptions),
      [methods.setValue, props.name]
   );

   return <CheckboxArrayOrderable value={value || []} setValue={setValue} {...props} />;
};

export default React.memo(HookCheckboxArrayOrderable);
